import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CookieStorageService } from '../../common/helpers/cookie-storage.service';
import { MediaUploadService } from '../../common/services/media-upload.service';
import { UserStatusService } from '../../common/services/user-status.service';
import { IUser } from '../../common/interfaces/user.interface';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-admin-impersonation-banner',
    standalone: true,
    imports: [
        CommonModule,
        NzButtonModule,
        NzIconModule
    ],
    templateUrl: './admin-impersonation-banner.component.html',
    styleUrl: './admin-impersonation-banner.component.scss'
})
export class AdminImpersonationBannerComponent implements OnInit, OnDestroy {
    private readonly cookieService = inject(CookieStorageService);
    private readonly router = inject(Router);
    private readonly nzMessageService = inject(NzMessageService);
    private readonly mediaUploadService = inject(MediaUploadService);
    private readonly userStatusService = inject(UserStatusService);

    isVisible = signal(false);
    impersonatedUserName = signal('');
    private checkInterval: any;

    ngOnInit(): void {
        this.checkImpersonationStatus();
        // Check periodically in case user logs in as another user
        this.checkInterval = setInterval(() => this.checkImpersonationStatus(), 1000);
    }

    ngOnDestroy(): void {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }
    }

    /**
     * Check if admin is currently impersonating a user
     */
    checkImpersonationStatus(): void {
        const adminAccessToken = this.cookieService.get<string>('adminAccessToken');
        const userInfo = this.cookieService.get<IUser>('userInfo');
        
        if (adminAccessToken) {
            this.isVisible.set(true);
            if (userInfo) {
                this.impersonatedUserName.set(`${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.email);
            }
        } else {
            this.isVisible.set(false);
        }
    }

    /**
     * Return to admin account
     */
    returnToAdmin(): void {
        const adminAccessToken = this.cookieService.get<string>('adminAccessToken');
        const adminUserInfo = this.cookieService.get<IUser>('adminUserInfo');

        if (!adminAccessToken) {
            this.nzMessageService.error('Unable to return to admin - session expired');
            return;
        }

        // Restore admin's token and user info
        this.cookieService.set('accessToken', adminAccessToken);
        
        if (adminUserInfo) {
            this.cookieService.set('userInfo', adminUserInfo);
            this.mediaUploadService.setUserImage(
                adminUserInfo.profilePhotoUrl 
                    ? `${environment.s3URL}${adminUserInfo.profilePhotoUrl}` 
                    : ''
            );
        }

        // Clear impersonation cookies
        this.cookieService.remove('adminAccessToken');
        this.cookieService.remove('adminUserInfo');

        // Restart status updates for admin
        this.userStatusService.startStatusUpdates();

        this.nzMessageService.success('Returned to admin account');
        this.isVisible.set(false);

        // Navigate to admin dashboard
        this.router.navigateByUrl('/user-management');
    }
}
